// One HTTP client for every Demand Management feature.
//
// Before this file there were four different base-URL conventions across seven
// clients — "/api", "", process.env.REACT_APP_API_BASE (unset, so requests literally
// went to "undefined/demands/...") and a window.__API_BASE__ ?? env ?? "" chain.
// Five of the seven resolved to "" and therefore called /demands/... , which the
// backend does not serve; each one caught the 404 and returned hardcoded demo data,
// so POC, Requirements, Security, TAB and Execution appeared to work while never
// reaching a server.
//
// Two rules follow from that, and they are the point of this module:
//   1. There is exactly one base. It is "/api" unless a deployment overrides it.
//   2. Failures throw. No client substitutes fake data for an error — a screen that
//      cannot load its data must say so, because silently-plausible output is worse
//      than a visible error.

const RAW_BASE =
  (typeof window !== "undefined" && window.__API_BASE__) ||
  process.env.REACT_APP_API_BASE ||
  "/api";

export const API_BASE = String(RAW_BASE).replace(/\/+$/, "");

const DEFAULT_TIMEOUT_MS = 30000;
// Triage, Discovery, Requirements, Security and TAB all call Claude server-side and
// routinely run past 30s.
export const AI_TIMEOUT_MS = 180000;

export class ApiError extends Error {
  constructor(message, { status, detail, path, method } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.path = path;
    this.method = method;
  }

  /** True when the server is reachable but the feature is switched off or unconfigured. */
  get isUnavailable() {
    return this.status === 503;
  }

  /** True when the feature exists but has no implementation for this target. */
  get isNotImplemented() {
    return this.status === 501;
  }

  /** True when an upstream system (ServiceNow, Claude, S3) failed rather than AIEA. */
  get isUpstream() {
    return this.status === 502;
  }
}

function buildUrl(path, query) {
  const url = `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
  });
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Fetch JSON, or throw ApiError.
 *
 * FastAPI reports problems as {"detail": ...} — a string for HTTPException and an
 * array of field errors for a 422. Both are unpacked into a readable message so the
 * UI can surface something better than "Request failed".
 */
export async function request(path, {
  method = "GET",
  body,
  query,
  timeout = DEFAULT_TIMEOUT_MS,
  signal,
} = {}) {
  const url = buildUrl(path, query);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  if (signal) signal.addEventListener("abort", () => controller.abort(), { once: true });

  let res;
  try {
    res = await fetch(url, {
      method,
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e.name === "AbortError") {
      throw new ApiError(`${method} ${path} timed out after ${Math.round(timeout / 1000)}s`,
        { path, method });
    }
    throw new ApiError(`${method} ${path} could not reach the server: ${e.message}`,
      { path, method });
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 204) return null;

  const text = await res.text().catch(() => "");
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!res.ok) {
    throw new ApiError(describeError(res, payload, text), {
      status: res.status,
      detail: payload?.detail ?? text,
      path,
      method,
    });
  }
  return payload;
}

function describeError(res, payload, text) {
  const detail = payload?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    // 422 validation errors: [{loc: ["body","field"], msg: "..."}]
    return detail
      .map((d) => `${(d.loc || []).slice(1).join(".") || "request"}: ${d.msg}`)
      .join("; ");
  }
  if (text && text.length < 300) return `HTTP ${res.status}: ${text}`;
  return `HTTP ${res.status} ${res.statusText}`;
}

export const get = (path, opts) => request(path, { ...opts, method: "GET" });
export const post = (path, body, opts) => request(path, { ...opts, method: "POST", body });
export const put = (path, body, opts) => request(path, { ...opts, method: "PUT", body });
export const patch = (path, body, opts) => request(path, { ...opts, method: "PATCH", body });
// `delete` is a reserved word, so this follows the usual JS convention of `del`.
export const del = (path, opts) => request(path, { ...opts, method: "DELETE" });

/** Path helper so no caller hand-builds a demand URL or forgets to encode the id. */
export const demandPath = (demandId, suffix = "") =>
  `/demands/${encodeURIComponent(demandId)}${suffix}`;
